
import { defineComponent, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { resolveListPageForRoute } from "@/core/helpers/list-page-context";
import {
  lengthValidationRules,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import {
  IDanhMucVatTuResponseBase,
  ITaiKhoanResponseBase,
} from "@/core/interfaces/ApiResponses";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useLoading } from "@/core/hooks/use-loading";
import DanhMucVatTuService from "@/core/services/DanhMucVatTu.service";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import BaseLoading from "@/components/shared/BaseLoading.vue";
import TaiKhoanSelectionV2 from "@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import { mapMutations } from "vuex";
import useFormInput from "@/core/hooks/use-form-input";
import { usePagination } from "@/core/hooks/use-pagination";
import DanhMucVatTuTable from "@/views/crafted/pages/danh-muc-vat-tu/danh-muc-vat-tu-table/DanhMucVatTuTable.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";

export default defineComponent({
  name: "danh-muc-vat-tu-form",

  components: {
    BaseLoading,
    ButtonCustom,
    DanhMucVatTuTable,
    DivCol,
    DivRow,
    LabelCustom,
    PageContentBase,
    Pagination,
    TaiKhoanSelectionV2,
  },

  setup() {
    const { goBack, replace, id } = useRouterCustom();
    const { formRef, isCreating } = useCreatingForm();
    const tenDanhMucInputRef = ref<HTMLInputElement | null>(null);
    const { focusFirstErrInputV2 } = useFormInput();
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const { setLoading, isLoading } = useLoading();
    const {
      currentPage,
      totalItems,
      perPage,
      loading: tableLoading,
    } = usePagination();
    const route = useRoute();
    currentPage.value = resolveListPageForRoute(route.query, {
      isCreate: isCreating.value,
    });
    const danhMucVatTuItems = ref<IDanhMucVatTuResponseBase[]>([]);

    const taiKhoanData = ref<{
      id: number;
      maTaiKhoan: string;
      maChiTiet?: string;
      tenTaiKhoan: string;
    }>({
      id: 0,
      maChiTiet: "",
      maTaiKhoan: "",
      tenTaiKhoan: "",
    });

    const danhMucVatTuFormValue = ref({
      ten_danh_muc: "",
      mo_ta: "",
      ma_danh_muc: "",
    });
    const taiKhoanSelectionKey = ref(0);

    const loadDanhMucVatTuDetail = async () => {
      if (isCreating.value || !id.value) {
        danhMucVatTuFormValue.value = {
          ten_danh_muc: "",
          mo_ta: "",
          ma_danh_muc: "",
        };
        taiKhoanData.value = {
          id: 0,
          maChiTiet: "",
          maTaiKhoan: "",
          tenTaiKhoan: "",
        };
        taiKhoanSelectionKey.value += 1;
        document.title = "Tạo mới - Nhóm vật tư - Đường sắt Hà Nội";
        setCurrentPageTitle("Tạo mới nhóm vật tư");

        return;
      }

      setLoading(true);

      try {
        const {
          data: {
            data: { ten_danh_muc, mo_ta, ma_danh_muc, tk_ketoan },
          },
        } = await DanhMucVatTuService.get(id.value);

        danhMucVatTuFormValue.value.ten_danh_muc = ten_danh_muc;
        danhMucVatTuFormValue.value.ma_danh_muc = ma_danh_muc;
        danhMucVatTuFormValue.value.mo_ta = mo_ta;

        document.title = `${ten_danh_muc} (${ma_danh_muc}) - Chỉnh sửa - Nhóm vật tư - Đường sắt Hà Nội`;
        setCurrentPageTitle(`${ten_danh_muc} (${ma_danh_muc})`);

        if (tk_ketoan) {
          const { matk, mact, id: id_tai_khoan, tentk } = tk_ketoan;

          taiKhoanData.value.maTaiKhoan = matk;
          taiKhoanData.value.maChiTiet = mact;
          taiKhoanData.value.id = id_tai_khoan;
          taiKhoanData.value.tenTaiKhoan = tentk;
          taiKhoanSelectionKey.value += 1;
        }
      } catch (error) {
        await swalErrNotification(
          error,
          "Xin lỗi, có vẻ có lỗi xảy ra khi lấy thông tin"
        );
      } finally {
        setLoading(false);
      }
    };

    onMounted(async () => {
      setBreadcrumbLinks([
        {
          text: "Nhóm vật tư",
          routeData: {
            name: "danh-sach-danh-muc-vat-tu",
          },
        },
        ...(isCreating.value
          ? []
          : [
              {
                text: "Chi tiết",
                routeData: {
                  name: "chi-tiet-danh-muc-vat-tu",
                  params: {
                    id: id.value,
                  },
                },
              },
            ]),
        {
          text: isCreating.value ? "Tạo mới" : "Chỉnh sửa",
        },
      ]);

      await loadDanhMucVatTuDetail();

      await nextTick();

      if (tenDanhMucInputRef.value) {
        const inputEl = tenDanhMucInputRef.value as HTMLInputElement;
        inputEl.focus();
        inputEl.select();
      }
    });

    watch(
      () => [id.value, isCreating.value] as const,
      async ([nextId, creating], [prevId, prevCreating]) => {
        if (creating) {
          currentPage.value = resolveListPageForRoute(route.query, {
            isCreate: true,
          });

          if (!prevCreating) {
            await loadDanhMucVatTuDetail();
          }

          return;
        }

        if (!nextId || (nextId === prevId && !prevCreating)) {
          return;
        }

        currentPage.value = resolveListPageForRoute(route.query, {
          isCreate: false,
        });
        await loadDanhMucVatTuDetail();
      }
    );

    return {
      ButtonTypeColors,
      ButtonsType,
      currentPage,
      danhMucVatTuItems,
      danhMucVatTuFormValue,
      danhMucVatTuId: id,
      focusFirstErrInputV2,
      formRef,
      goBack,
      isCreating,
      isLoading,
      perPage,
      replace,
      setLoading,
      tableLoading,
      taiKhoanData,
      taiKhoanSelectionKey,
      tenDanhMucInputRef,
      totalItems,
    };
  },

  data() {
    return {
      rules: {
        ten_danh_muc: [
          ...lengthValidationRules({
            name: "Tên nhóm vật tư",
          }),
        ],
        mo_ta: [
          ...lengthValidationRules({
            isRequired: false,
            max: 160,
            name: "Mô tả nhóm vật tư",
          }),
        ],
      },
    };
  },

  async mounted() {
    await this.fetchData();
  },

  watch: {
    async currentPage() {
      await this.fetchData();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchData();

        return;
      }

      this.currentPage = 1;
    },
  },

  methods: {
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),
    async fetchData() {
      try {
        this.tableLoading = true;

        const {
          data: {
            data: { data, total },
          },
        } = await DanhMucVatTuService.list(this.currentPage, this.perPage);

        this.totalItems = total;
        this.danhMucVatTuItems = data;
        this.tableLoading = false;
      } catch (e) {
        this.tableLoading = false;

        await swalNotification(
          "Có lỗi xảy ra khi lấy danh sách nhóm vật tư",
          "error"
        );
      }
    },

    async refreshFirstPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;

        return;
      }

      await this.fetchData();
    },

    async clear() {
      this.danhMucVatTuFormValue.ten_danh_muc = "";
      this.danhMucVatTuFormValue.mo_ta = "";
      this.taiKhoanData.id = 0;
      this.taiKhoanData.maChiTiet = "";
      this.taiKhoanData.maTaiKhoan = "";
      this.taiKhoanData.tenTaiKhoan = "";
      this.taiKhoanSelectionKey++;

      await nextTick();
      this.formRef?.clearValidate();
    },

    onSelectTaiKhoan({
      taiKhoanItem: {
        id: id_tai_khoan,
        ma_tai_khoan,
        ma_chi_tiet,
        ten_tai_khoan,
      },
    }: {
      taiKhoanItem: ITaiKhoanResponseBase;
    }) {
      this.taiKhoanData.maTaiKhoan = ma_tai_khoan;
      this.taiKhoanData.tenTaiKhoan = ten_tai_khoan;
      this.taiKhoanData.id = id_tai_khoan;
      this.taiKhoanData.maChiTiet = ma_chi_tiet
        ? ma_chi_tiet.toString()
        : "";
    },

    onClearTaiKhoan(type: "tai_khoan" | "chi_tiet") {
      this.taiKhoanData.maChiTiet = "";

      if (type === "tai_khoan") {
        this.taiKhoanData.id = 0;
        this.taiKhoanData.maTaiKhoan = "";
        this.taiKhoanData.tenTaiKhoan = "";
      }
    },

    submit(shouldContinue = false) {
      if (!this.formRef) {
        return;
      }

      if (!this.taiKhoanData.maTaiKhoan) {
        swalErrNotification(null, "Bắt buộc phải chọn mã tài khoản");

        return;
      }

      this.formRef.validate(async (valid) => {
        if (valid) {
          this.setDialog({
            message: this.isCreating
              ? shouldContinue
                ? "Tạo mới nhóm vật tư với những thông tin này và tiếp tục tạo mới không?"
                : "Tạo mới nhóm vật tư với những thông tin này không?"
              : "Bạn có chắc chắn muốn lưu chỉnh sửa cho nhóm vật tư này không?",
            onConfirm: async () => {
              this.closeDialog();
              await nextTick();
              this.setLoading(true);

              try {
                if (this.isCreating) {
                  const {
                    data: {
                      data: { id },
                    },
                  } = await DanhMucVatTuService.create({
                    ten_danh_muc: this.danhMucVatTuFormValue.ten_danh_muc,
                    mo_ta: this.danhMucVatTuFormValue.mo_ta,
                    id_cha: 0,
                    id_tai_khoan: this.taiKhoanData.id,
                    ma_tai_khoan: this.taiKhoanData.maTaiKhoan,
                    ma_chi_tiet: this.taiKhoanData.maChiTiet,
                    ten_tai_khoan: this.taiKhoanData.tenTaiKhoan,
                  });

                  this.setLoading(false);

                  await swalNotification("Tạo mới nhóm vật tư thành công");

                  if (shouldContinue) {
                    this.clear();
                    this.formRef?.resetFields();
                    await this.refreshFirstPage();

                    return;
                  }

                  this.danhMucVatTuId = id;
                } else {
                  await DanhMucVatTuService.update({
                    ten_danh_muc: this.danhMucVatTuFormValue.ten_danh_muc,
                    mo_ta: this.danhMucVatTuFormValue.mo_ta,
                    id_cha: 0,
                    id_tai_khoan: this.taiKhoanData.id,
                    ma_tai_khoan: this.taiKhoanData.maTaiKhoan,
                    ma_chi_tiet: this.taiKhoanData.maChiTiet,
                    ten_tai_khoan: this.taiKhoanData.tenTaiKhoan,
                    id: this.danhMucVatTuId,
                  });

                  this.setLoading(false);

                  await swalNotification("Chỉnh sửa nhóm vật tư thành công");

                  if (shouldContinue) {
                    await this.refreshFirstPage();

                    return;
                  }
                }

                await this.replace("/quan-ly/danh-muc-vat-tu");
              } catch (error) {
                this.setLoading(false);

                await swalErrNotification(
                  error,
                  "Có lỗi xảy ra khi lưu nhóm vật tư"
                );
              }
            },
          });
        } else {
          await this.focusFirstErrInputV2();

          return false;
        }
      });
    },
  },
});
